import './cell.js';

import { ShadowlessElement, WithDisposable } from '@blocksuite/block-std';
import { SignalWatcher } from '@lit-labs/preact-signals';
import { css, html as litHtml } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import { CenterPeekIcon } from '../../../common/icons/index.js';
import {
  popTagSelect,
  type SelectTag,
} from '../../../utils/tags/multi-tag-select.js';

const MoreIcon = litHtml`<svg width="14" height="14" viewBox="0 0 16 16" fill="var(--affine-icon-color)"><circle cx="8" cy="3" r="1.5"/><circle cx="8" cy="8" r="1.5"/><circle cx="8" cy="13" r="1.5"/></svg>`;
import {
  generateAvatarUrl,
  getColorFromName,
  getInitials,
  normalizeEmail,
} from '@pique/core/modules/properties/services/attendee-id-utils';
import type {
  SerializedAttendee,
  WorkspaceAffineProperties,
} from '@pique/core/modules/properties/services/schema';

import type { DataViewRenderer } from '../../../data-view.js';
import type { KanbanColumn, KanbanSingleView } from './kanban-view-manager.js';
import { openDetail, popCardMenu } from './menu.js';

const styles = css`
  affine-data-view-kanban-card {
    display: flex;
    position: relative;
    flex-direction: column;
    border: 1px solid var(--affine-border-color);
    box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    transition: background-color 100ms ease-in-out;
    background-color: var(--affine-background-kanban-card-color);
    -webkit-user-select: none;
    -webkit-touch-callout: none;
  }

  affine-data-view-kanban-card:hover {
    background-color: var(--affine-hover-color);
  }

  affine-data-view-kanban-card .card-header {
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Pique customization: tighter card header spacing and larger title for todo focus */
  affine-data-view-kanban-card .card-header {
    padding: 10px 12px 18px 12px;
    gap: 6px;
  }

  affine-data-view-kanban-card .card-header-title uni-lit {
    width: 100%;
  }

  .card-header.has-divider {
    border-bottom: 0.5px solid var(--affine-border-color);
  }

  affine-data-view-kanban-card .card-header-title {
    font-size: var(--data-view-cell-text-size);
    line-height: var(--data-view-cell-text-line-height);
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    user-select: none;
  }

  affine-data-view-kanban-card .card-body {
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 4px;
  }

  /* Pique customizations: keep Kanban cards clean by hiding property rows on the card.
     Full details remain available in the detail panel. */
  affine-data-view-kanban-card .card-body {
    display: none !important;
  }

  affine-data-view-kanban-card:hover .card-ops {
    visibility: visible;
  }

  .card-ops {
    position: absolute;
    right: 8px;
    top: 8px;
    visibility: hidden;
    display: flex;
    gap: 4px;
    cursor: pointer;
  }

  .card-op {
    display: flex;
    position: relative;
    padding: 3px;
    border-radius: 50%;
    box-shadow: 0px 0px 3px 0px rgba(66, 65, 73, 0.12);
    background-color: var(--affine-background-primary-color);
  }

  .card-op:hover:before {
    content: '';
    border-radius: 50%;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: var(--affine-hover-color);
  }

  .card-op svg {
    fill: var(--affine-icon-color);
    color: var(--affine-icon-color);
    width: 14px;
    height: 14px;
  }

  .card-op.open-btn {
    border: 1px solid var(--pique-green-border);
    box-shadow: var(--pique-green-shadow-medium);
    color: var(--affine-text-primary-color);
    background: linear-gradient(
      90deg,
      rgb(200, 240, 215) 0%,
      rgb(180, 230, 200) 100%
    );
  }

  .card-op.open-btn svg {
    fill: none;
    color: var(--affine-text-primary-color);
  }

  .card-op.open-btn svg path {
    fill: var(--affine-text-primary-color);
  }

  .card-op.open-btn:hover {
    border-color: var(--pique-green-border-hover);
    box-shadow: var(--pique-green-shadow-strong);
    transform: translateY(-1px);
    background: linear-gradient(
      90deg,
      rgb(180, 230, 200) 0%,
      rgb(160, 220, 185) 100%
    );
  }

  .card-op.open-btn:hover:before {
    background-color: transparent;
  }

  .card-op.open-btn:hover svg path {
    fill: var(--affine-text-primary-color);
  }

  /* Dark mode: more saturated green */
  html[data-theme='dark'] .card-op.open-btn {
    background: linear-gradient(
      90deg,
      rgb(46, 164, 93) 0%,
      rgb(36, 144, 78) 100%
    );
  }

  html[data-theme='dark'] .card-op.open-btn:hover {
    background: linear-gradient(
      90deg,
      rgb(56, 174, 103) 0%,
      rgb(46, 154, 88) 100%
    );
  }

  .card-bottom-right {
    position: absolute;
    right: 4px;
    bottom: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .card-assignee-avatars {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  .card-assignee-avatars:hover {
    background-color: var(--affine-hover-color);
  }

  .card-assignee-avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 600;
    color: #fff;
    border: 1.5px solid var(--affine-background-kanban-card-color);
    background-size: cover;
    background-position: center;
    flex-shrink: 0;
  }

  .card-assignee-avatar:not(:first-child) {
    margin-left: -6px;
  }

  .card-assignee-count {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
    font-weight: 600;
    color: var(--affine-text-secondary-color);
    background-color: var(--affine-hover-color);
    border: 1.5px solid var(--affine-background-kanban-card-color);
    margin-left: -6px;
  }

  .card-more-btn {
    display: flex;
    cursor: pointer;
    visibility: hidden;
    opacity: 0;
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  .card-more-btn:hover {
    background-color: var(--affine-hover-color);
  }

  affine-data-view-kanban-card:hover .card-more-btn {
    visibility: visible;
    opacity: 1;
  }

  .card-reminder-chip {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 1px 6px;
    border-radius: 10px;
    background-color: var(--affine-brand-color);
    color: var(--affine-pure-white, #fff);
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
    cursor: pointer;
  }

  .card-reminder-chip:hover {
    filter: brightness(0.96);
  }

  .card-reminder-chip svg {
    width: 10px;
    height: 10px;
  }
`;

@customElement('affine-data-view-kanban-card')
export class KanbanCard extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  private clickEdit = (e: MouseEvent) => {
    e.stopPropagation();
    const selection = this.getSelection();
    if (selection) {
      openDetail(this.dataViewEle, this.cardId, selection);
      window.dispatchEvent(new CustomEvent('pique-kanban-card-opened'));
    }
  };

  private clickMore = (e: MouseEvent) => {
    e.stopPropagation();
    const selection = this.getSelection();
    if (selection) {
      selection.selection = {
        selectionType: 'card',
        cards: [
          {
            groupKey: this.groupKey,
            cardId: this.cardId,
          },
        ],
      };
      popCardMenu(this.dataViewEle, this, this.cardId, selection);
    }
  };

  private contextMenu = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const selection = this.getSelection();
    if (selection) {
      selection.selection = {
        selectionType: 'card',
        cards: [
          {
            groupKey: this.groupKey,
            cardId: this.cardId,
          },
        ],
      };
      const target = e.target as HTMLElement;
      const ref = target.closest('affine-data-view-kanban-cell') ?? this;
      popCardMenu(this.dataViewEle, ref, this.cardId, selection);
    }
  };

  static override styles = styles;

  private getSelection() {
    return this.closest('affine-data-view-kanban')?.selectionController;
  }

  private getPageAttendees(): SerializedAttendee[] {
    try {
      const doc = (this.view as unknown as { manager: { dataSource: { doc: unknown } } }).manager.dataSource.doc as { id: string; collection?: { doc?: { getMapProxy: <K, V>(key: K) => V | undefined } } } | undefined;
      if (!doc) return [];
      const propertiesProxy = doc.collection?.doc?.getMapProxy<
        'affine:workspace-properties',
        WorkspaceAffineProperties
      >('affine:workspace-properties');
      const pageId = doc.id;
      const pageProps = propertiesProxy?.pageProperties?.[pageId];
      const attendeesValue = pageProps?.system?.attendees?.value as
        | SerializedAttendee[]
        | undefined;
      return Array.isArray(attendeesValue) ? attendeesValue : [];
    } catch {
      return [];
    }
  }

  private getReminderColumn(): KanbanColumn | undefined {
    const columnIds = this.view.columnsWithoutFilter$.value;
    for (const id of columnIds) {
      const col = this.view.columnGet(id);
      if (col.name === 'Remind Later' || col.name === 'remindLater') {
        return col;
      }
    }
    return undefined;
  }

  private getReminderData(): string[] {
    const column = this.getReminderColumn();
    if (!column) return [];
    const cell = column.cellGet(this.cardId);
    const rawValue = cell?.value$?.value;
    return Array.isArray(rawValue) ? rawValue : [];
  }

  private getReminderSelectData(): {
    options: SelectTag[];
    value: string[];
    column: KanbanColumn;
  } | null {
    const column = this.getReminderColumn();
    if (!column) return null;
    const cell = column.cellGet(this.cardId);
    const rawValue = cell?.value$?.value;
    const value: string[] = Array.isArray(rawValue) ? rawValue : [];
    const data = column.data$?.value as { options?: SelectTag[] } | undefined;
    const options: SelectTag[] = data?.options ?? [];
    return { options, value, column };
  }

  private clickReminderChip = (e: MouseEvent) => {
    e.stopPropagation();
    const reminderData = this.getReminderSelectData();
    if (!reminderData || this.view.readonly$.value) return;

    const { options, value, column } = reminderData;
    const target = e.currentTarget as HTMLElement;
    const labelMap: Record<string, string> = {
      one_hour: '1 Hr',
      three_hours: '3 Hr',
      tomorrow: 'Tomorrow',
      next_week: '1 Wk',
    };
    const enrichedOptions = options.map(opt => ({
      ...opt,
      displayName: labelMap[opt.value] || opt.displayName,
    }));

    this._disposables.add({
      dispose: popTagSelect(target, {
        mode: 'single',
        options: enrichedOptions,
        onOptionsChange: (newOptions: SelectTag[]) => {
          column.updateData(data => ({
            ...data,
            options: newOptions,
          }));
        },
        value,
        onChange: (ids: string[]) => {
          const cell = column.cellGet(this.cardId);
          if (cell) {
            cell.setValue(ids);
          }
          this.requestUpdate();
        },
        onComplete: () => {},
        minWidth: 220,
        placement: 'right-start',
        offsetMainAxis: 8,
      }),
    });
  };

  private getAttendeeAvatar(email: string): string | null {
    const pageAttendees = this.getPageAttendees();
    const norm = normalizeEmail(email);
    const attendee = pageAttendees.find(
      a => normalizeEmail(a.email || '') === norm
    );
    return attendee?.avatar ?? null;
  }

  private renderBody(columns: KanbanColumn[]) {
    if (columns.length === 0) {
      return '';
    }
    return html` <div class="card-body">
      ${repeat(
        columns,
        v => v.id,
        column => {
          if (this.view.isInHeader(column.id)) {
            return '';
          }
          return html` <affine-data-view-kanban-cell
            .contentOnly="${false}"
            data-column-id="${column.id}"
            .view="${this.view}"
            .groupKey="${this.groupKey}"
            .column="${column}"
            .cardId="${this.cardId}"
          ></affine-data-view-kanban-cell>`;
        }
      )}
    </div>`;
  }

  private renderHeader(columns: KanbanColumn[]) {
    if (!this.view.hasHeader(this.cardId)) {
      return '';
    }
    const classList = classMap({
      'card-header': true,
      'has-divider': columns.length > 0,
    });
    return html` <div class="${classList}">${this.renderTitle()}</div> `;
  }

  private getAssigneeColumn(): KanbanColumn | undefined {
    const columns = this.view.columnManagerList$.value;
    return columns.find(col => {
      const name = col.name;
      return (
        name === "Who's Responsible?" ||
        name === 'Assignee' ||
        name === 'Assigned To' ||
        name === 'Assign To'
      );
    });
  }

  private getAssigneeData(): {
    options: SelectTag[];
    value: string[];
    column: KanbanColumn;
  } | null {
    const column = this.getAssigneeColumn();
    if (!column) return null;
    const cell = column.cellGet(this.cardId);
    const rawValue = cell?.value$?.value;
    const value: string[] = Array.isArray(rawValue) ? rawValue : [];
    const data = column.data$?.value as { options?: SelectTag[] } | undefined;
    const options: SelectTag[] = data?.options ?? [];
    return { options, value, column };
  }

  private clickAssigneeAvatar = (e: MouseEvent) => {
    e.stopPropagation();
    const assigneeData = this.getAssigneeData();
    if (!assigneeData || this.view.readonly$.value) return;

    const { options, value, column } = assigneeData;
    const target = e.currentTarget as HTMLElement;

    const enrichedOptions = options.map(opt => ({
      ...opt,
      avatar: this.getAttendeeAvatar(opt.value) ?? opt.avatar,
    }));

    this._disposables.add({
      dispose: popTagSelect(target, {
        options: enrichedOptions,
        onOptionsChange: (newOptions: SelectTag[]) => {
          column.updateData(data => ({
            ...data,
            options: newOptions,
          }));
        },
        value,
        onChange: (ids: string[]) => {
          const cell = column.cellGet(this.cardId);
          if (cell) {
            cell.setValue(ids);
          }
        },
        onComplete: () => {},
        minWidth: 240,
        placement: 'right-start',
        offsetMainAxis: 8,
      }),
    });
  };

  private renderAssigneeAvatars() {
    const assigneeData = this.getAssigneeData();
    if (!assigneeData) return '';

    const { options, value } = assigneeData;
    if (value.length === 0) return '';

    const maxDisplay = 3;
    const selectedOptions = value
      .map(id => options.find(opt => opt.id === id))
      .filter((opt): opt is SelectTag => !!opt);

    if (selectedOptions.length === 0) return '';

    const displayOptions = selectedOptions.slice(0, maxDisplay);
    const remainingCount = Math.max(0, selectedOptions.length - maxDisplay);

    return html`
      <div class="card-assignee-avatars" @click="${this.clickAssigneeAvatar}">
        ${displayOptions.map((opt, index) => {
          const displayName = opt.displayName || opt.value.split('@')[0];
          const realAvatar = this.getAttendeeAvatar(opt.value) ?? opt.avatar;
          const avatarUrl = generateAvatarUrl(displayName, realAvatar);
          const initials = getInitials(displayName);
          const bgColor = getColorFromName(displayName);
          return html`
            <div
              class="card-assignee-avatar"
              style=${styleMap({
                backgroundColor: avatarUrl ? 'transparent' : bgColor,
                backgroundImage: avatarUrl ? `url('${avatarUrl}')` : 'none',
                zIndex: String(displayOptions.length - index),
              })}
              title="${opt.value}"
            >
              ${!avatarUrl ? initials : ''}
            </div>
          `;
        })}
        ${remainingCount > 0
          ? html` <div class="card-assignee-count">+${remainingCount}</div> `
          : ''}
      </div>
    `;
  }

  private renderReminder() {
    const reminder = this.getReminderData();
    if (reminder.length === 0) return '';
    const selected = reminder[0] || '';
    const labelMap: Record<string, string> = {
      one_hour: '1 Hr',
      three_hours: '3 Hr',
      tomorrow: 'Tomorrow',
      next_week: '1 Wk',
    };
    const label = labelMap[selected] || selected;
    const bellIcon = html`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>`;
    return html`
      <div
        class="card-reminder-chip"
        title="Reminder: ${label}"
        @click="${this.clickReminderChip}"
      >
        ${bellIcon}
        <span>${label}</span>
      </div>
    `;
  }

  private renderOps() {
    if (this.view.readonly$.value) {
      return;
    }
    return html`
      <div class="card-ops">
        <div class="card-op open-btn" @click="${this.clickEdit}">
          ${CenterPeekIcon}
          <affine-tooltip tip-position="top">Open</affine-tooltip>
        </div>
      </div>
    `;
  }

  private renderBottomRight() {
    return html`
      <div class="card-bottom-right">
        ${this.renderReminder()} ${this.renderAssigneeAvatars()}
        ${!this.view.readonly$.value
          ? html`<div class="card-more-btn" @click="${this.clickMore}">
              ${MoreIcon}
            </div>`
          : ''}
      </div>
    `;
  }

  private renderTitle() {
    const title = this.view.getHeaderTitle(this.cardId);
    if (!title) {
      return;
    }
    return html` <div class="card-header-title">
      <affine-data-view-kanban-cell
        .contentOnly="${true}"
        data-column-id="${title.id}"
        .view="${this.view}"
        .groupKey="${this.groupKey}"
        .column="${title}"
        .cardId="${this.cardId}"
      ></affine-data-view-kanban-cell>
    </div>`;
  }

  override connectedCallback() {
    super.connectedCallback();
    if (this.view.readonly$.value) {
      return;
    }
    this._disposables.addFromEvent(this, 'contextmenu', e => {
      this.contextMenu(e);
    });
    this._disposables.addFromEvent(this, 'click', e => {
      if (e.shiftKey) {
        this.getSelection()?.shiftClickCard(e);
        return;
      }
      const selection = this.getSelection();
      const preSelection = selection?.selection;

      if (preSelection?.selectionType !== 'card') return;

      if (selection) {
        selection.selection = undefined;
      }
      this.dataViewEle.openDetailPanel({
        view: this.view,
        rowId: this.cardId,
        onClose: () => {
          if (selection) {
            selection.selection = preSelection;
          }
        },
      });
      window.dispatchEvent(new CustomEvent('pique-kanban-card-opened'));
    });
  }

  override render() {
    const columns = this.view.columnManagerList$.value.filter(
      v => !this.view.isInHeader(v.id)
    );
    if (this.isFocus) {
      this.style.border = '1px solid var(--affine-primary-color)';
    } else if (this.isHighlighted) {
      this.style.border = '1.5px solid var(--affine-brand-color)';
    } else {
      this.style.border = '';
    }
    return html`
      ${this.renderHeader(columns)} ${this.renderBody(columns)}
      ${this.renderOps()} ${this.renderBottomRight()}
    `;
  }

  @property({ attribute: false })
  accessor cardId!: string;

  @property({ attribute: false })
  accessor dataViewEle!: DataViewRenderer;

  @property({ attribute: false })
  accessor groupKey!: string;

  @state()
  accessor isFocus = false;

  @property({ attribute: false })
  accessor isHighlighted = false;

  @property({ attribute: false })
  accessor view!: KanbanSingleView;
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-data-view-kanban-card': KanbanCard;
  }
}
